import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import theme from 'theme';

import DefaultButton from 'components/buttons/DefaultButton';
import SectionTitle from 'components/previewPages/SectionTitle';
import patterns from 'helpers/patterns';

type EmailSectionProps = {
  isMobile?: boolean;
};

const EMAIL_INPUT_ID = 'EMAIL_INPUT_ID';

const EmailSection = (
  { isMobile = false }: EmailSectionProps,
  ref: React.Ref<HTMLDivElement>
) => {
  const { register, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      [EMAIL_INPUT_ID]: '',
    },
  });

  const [activeInput, setActiveInput] = useState(false);

  const email = watch()[EMAIL_INPUT_ID] as string;
  const isValid = !!email.match(patterns.emailPattern);

  return (
    <Container ref={ref} $isMobile={isMobile}>
      <SectionTitle isMobile={isMobile}>
        Start your free 1 month trial!
      </SectionTitle>
      <Subtitle $isMobile={isMobile}>
        Enter your email to activate your unique offer code
      </Subtitle>
      <div>
        <InputContainer $isMobile={isMobile}>
          <Label
            data-testid="email-label"
            $active={activeInput}
            htmlFor={EMAIL_INPUT_ID}
          >
            Enter email
          </Label>
          <StyledInput
            id={EMAIL_INPUT_ID}
            onFocus={() => setActiveInput(true)}
            {...register(EMAIL_INPUT_ID, {
              required: true,
              pattern: patterns.emailPattern,
            })}
          />
        </InputContainer>
        <WrapperButton>
          <DefaultButton
            text="send offer code"
            textColor={theme.colors.white}
            isActive={isValid}
            withShadow
            buttonColor={isValid ? theme.colors.funtainBlue : theme.colors.gray}
            isUppercase
            buttonSize={isMobile ? 'md' : 'xl'}
            borderSize={isMobile ? 'md' : 'xl'}
            isSubmit
          />
        </WrapperButton>
      </div>
    </Container>
  );
};

const WrapperButton = styled.div`
  max-width: 305px;
  margin: auto;
  margin-bottom: 81px;
  display: flex;
  justify-content: center;

  @media (width <= 480px) {
    margin-bottom: 41px;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  height: 100%;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  outline: none;
  border: none;
  padding-top: 25px;
  padding-left: 1px;
  box-sizing: border-box;

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus {
    -webkit-text-fill-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 1000px #fff inset;
  }
`;

const Label = styled.label<{ $active: boolean }>`
  font-size: ${({ $active }) => ($active ? '12px' : '16px')};
  color: ${({ theme }) => theme.colors.funtainBlue};
  position: absolute;
  transform: translateY(-50%);
  transition:
    all 0.5s,
    color 0s;
  top: ${({ $active }) => ($active ? '20px' : '50%')};
`;

const InputContainer = styled.div<{ $isMobile: boolean }>`
  position: relative;
  margin: 73px auto 40px;
  max-width: 600px;
  padding-left: 20px;
  padding-right: 6px;
  box-sizing: border-box;
  height: 60px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.funtainBlue};
  padding-bottom: 5px;
  background: white;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      margin: 40px auto;
      max-width: 335px;
    `}
`;

const Subtitle = styled.p<{ $isMobile: boolean }>`
  font-size: 20px;
  font-weight: 300;
  line-height: 35px;
  color: ${({ theme }) => theme.colors.topicBlue};
  text-align: center;
  margin-bottom: 34px;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      font-size: 14px;
      line-height: 20px;
    `}
`;

const Container = styled.section<{ $isMobile: boolean }>`
  max-width: 1400px;
  margin: 0 auto;
  padding: 78px 0;
  ${({ $isMobile }) =>
    $isMobile &&
    css`
      padding: 34px 0;
    `}
`;

export default React.forwardRef(EmailSection);
